import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { Leaf, ArrowLeft, Mail, Lock, User, AlertCircle, CheckCircle2, Eye, EyeOff, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import { useBiometrics } from '../hooks/useBiometrics';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') || 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [consentTermsAI, setConsentTermsAI] = useState(false);
  const [consentDataProcessing, setConsentDataProcessing] = useState(false);
  const [consentAge, setConsentAge] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const { isAvailable: biometricsAvailable, isEnabled: biometricsEnabled, enableBiometrics, authenticateWithBiometrics } = useBiometrics();
  
  // Policy versions for audit trail
  const TERMS_VERSION = "1.0.0";
  const PRIVACY_VERSION = "1.0.0";
  
  const navigate = useNavigate();
  const { login, biometricLogin, register, user } = useAuth();
  const redirect = searchParams.get('redirect') || '/dashboard';

  const handleBiometricLogin = useCallback(async () => {
    setBiometricLoading(true);
    setError('');
    try {
      const result = await authenticateWithBiometrics();
      if (result.success && result.userId) {
        await biometricLogin(result.userId);
        toast.success('Welcome back!');
        navigate(redirect);
      } else {
        setError('Biometric authentication failed. Please use your email and password.');
      }
    } catch (err) {
      console.error('Biometric login error:', err);
      setError('Biometric login failed. Please use your email and password.');
    } finally {
      setBiometricLoading(false);
    }
  }, [authenticateWithBiometrics, biometricLogin, navigate, redirect]);

  useEffect(() => {
    if (user) {
      navigate(redirect);
    }
  }, [user, navigate, redirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const userData = await login(email, password);
        toast.success('Welcome back!');
        // If biometrics available but not enabled, prompt user
        if (biometricsAvailable && !biometricsEnabled) {
          setPendingRedirect(redirect);
          setShowBiometricPrompt(true);
        } else {
          navigate(redirect);
        }
      } else {
        // Include consent data with timestamps and versions - formatted as array for backend
        const consentData = [
          {
            type: "terms_ai",
            accepted: consentTermsAI,
            version: TERMS_VERSION,
            timestamp: new Date().toISOString()
          },
          {
            type: "data_processing",
            accepted: consentDataProcessing,
            version: PRIVACY_VERSION,
            timestamp: new Date().toISOString()
          },
          {
            type: "age_verification",
            accepted: consentAge,
            version: "1.0",
            timestamp: new Date().toISOString()
          }
        ];
        await register(name, email, password, consentData);
        setRegistrationSuccess(true);
        toast.success('Account created! Please check your email to verify.');
      }
    } catch (err) {
      console.error('Auth error:', err);
      let message = 'An error occurred. Please try again.';
      
      if (err.code === 'ECONNABORTED') {
        message = 'Connection timed out. Please check your internet and try again.';
      } else if (!navigator.onLine) {
        message = 'No internet connection. Please check your network.';
      } else if (err.response?.status === 401) {
        message = 'Invalid email or password.';
      } else if (err.response?.status === 403) {
        message = err.response?.data?.detail || 'Please verify your email before signing in. Check your inbox for the verification link.';
      } else if (err.response?.status === 400) {
        message = err.response?.data?.detail || 'Invalid request. Please check your input.';
      } else if (err.response?.status === 409) {
        message = 'An account with this email already exists.';
      } else if (err.response?.status === 502 || err.response?.status === 503) {
        message = 'Server is restarting. Please wait a moment and try again.';
      } else if (err.response?.status >= 500) {
        message = 'Server is temporarily unavailable. Please try again in a moment.';
      } else if (err.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (!err.response) {
        message = 'Unable to reach the server. Please check your connection and try again.';
      }
      
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex">
      {/* Biometric Enrollment Prompt */}
      <AnimatePresence>
        {showBiometricPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
              data-testid="biometric-enrollment-modal"
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Fingerprint className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-center text-[#1A1A1A] mb-2">
                Enable Quick Sign-In
              </h2>
              <p className="text-sm text-muted-foreground text-center mb-6">
                Use Face ID or Touch ID to sign in instantly next time — no password needed.
              </p>
              <div className="space-y-3">
                <Button
                  className="w-full h-12 btn-primary gap-2"
                  data-testid="biometric-enable-btn"
                  disabled={biometricLoading}
                  onClick={async () => {
                    setBiometricLoading(true);
                    try {
                      const result = await enableBiometrics(user?.id, user?.name || user?.email || name);
                      if (result.success) {
                        toast.success('Biometric login enabled!');
                      } else {
                        toast.error('Could not enable biometrics. You can try again from Profile.');
                      }
                    } catch (err) {
                      toast.error('Something went wrong. You can enable this later from Profile.');
                    } finally {
                      setBiometricLoading(false);
                      setShowBiometricPrompt(false);
                      navigate(pendingRedirect || '/dashboard');
                    }
                  }}
                >
                  <Fingerprint className="w-5 h-5" />
                  {biometricLoading ? 'Setting up...' : 'Enable Face ID / Touch ID'}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  data-testid="biometric-skip-btn"
                  onClick={() => {
                    setShowBiometricPrompt(false);
                    navigate(pendingRedirect || '/dashboard');
                  }}
                >
                  Maybe Later
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-20">
        <div className="max-w-md w-full mx-auto">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>

          <div className="mb-8">
            <Logo size="large" linkTo="/" />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h1 className="text-3xl font-bold text-[#1A1A1A] mb-2">
                {mode === 'login' ? 'Welcome back' : 'Create your account'}
              </h1>
              <p className="text-muted-foreground mb-8">
                {mode === 'login' 
                  ? 'Sign in to access your personalized recommendations' 
                  : 'Start your journey to better health today'}
              </p>

              {error && (
                <div className="flex items-center gap-2 p-4 mb-6 rounded-lg bg-destructive/10 text-destructive">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {registrationSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#1A1A1A] mb-2">Check your email!</h2>
                  <p className="text-muted-foreground mb-6">
                    We've sent a verification link to <strong>{email}</strong>. 
                    Please click the link to activate your account.
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Didn't receive the email? Check your spam folder.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setMode('login');
                      setRegistrationSuccess(false);
                    }}
                  >
                    Go to Sign In
                  </Button>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {mode === 'register' && (
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#1A1A1A]">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="name"
                        data-testid="auth-name-input"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="John Doe"
                        className="pl-10 h-12 bg-white border-border focus:border-primary"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1A1A1A]">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      data-testid="auth-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 h-12 bg-white border-border focus:border-primary"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1A1A1A]">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="password"
                      data-testid="auth-password-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12 bg-white border-border focus:border-primary"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <Eye className="w-5 h-5" />
                      ) : (
                        <EyeOff className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Legal Consent Checkboxes - Only for Registration */}
                {mode === 'register' && (
                  <div className="space-y-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Required Agreements</p>
                    
                    {/* Checkbox A: Terms & AI Acknowledgement */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="consent-terms-ai"
                        checked={consentTermsAI}
                        onChange={(e) => setConsentTermsAI(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                        data-testid="consent-terms-ai"
                      />
                      <label htmlFor="consent-terms-ai" className="text-sm text-muted-foreground leading-relaxed">
                        I have read and agree to the{' '}
                        <Link to="/terms" className="text-primary hover:underline font-medium" target="_blank" rel="noopener noreferrer">
                          Terms and Conditions
                        </Link>. I understand that Vitala.me provides AI-generated supplement suggestions only, which are experimental in nature and <strong>do not constitute medical advice</strong> or a clinical prescription. <span className="text-red-500">*</span>
                      </label>
                    </div>
                    
                    {/* Checkbox B: Data Processing & GDPR */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="consent-data-processing"
                        checked={consentDataProcessing}
                        onChange={(e) => setConsentDataProcessing(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                        data-testid="consent-data-processing"
                      />
                      <label htmlFor="consent-data-processing" className="text-sm text-muted-foreground leading-relaxed">
                        I consent to the processing of my health data as outlined in the{' '}
                        <Link to="/privacy" className="text-primary hover:underline font-medium" target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </Link>. I understand my data is processed by a stateless AI (GPT-5.2) at inference time and is <strong>not used for model training</strong>. I am aware I can export or delete my data at any time. <span className="text-red-500">*</span>
                      </label>
                    </div>
                    
                    {/* Checkbox C: Age Verification */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="consent-age"
                        checked={consentAge}
                        onChange={(e) => setConsentAge(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                        data-testid="consent-age"
                      />
                      <label htmlFor="consent-age" className="text-sm text-muted-foreground leading-relaxed">
                        I confirm that I am <strong>18 years of age or older</strong>. <span className="text-red-500">*</span>
                      </label>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="auth-submit-button"
                  disabled={loading || (mode === 'register' && (!consentTermsAI || !consentDataProcessing || !consentAge))}
                  className="w-full btn-primary h-12 text-base"
                >
                  {loading ? 'Please wait...' : (mode === 'login' ? 'Sign In' : 'Create Account')}
                </Button>
              </form>
              )}

              {!registrationSuccess && (
              <>
              {/* Biometric Login Button */}
              {mode === 'login' && biometricsEnabled && (
                <div className="mt-4">
                  <div className="relative flex items-center justify-center my-3">
                    <div className="border-t border-border flex-1" />
                    <span className="px-3 text-xs text-muted-foreground">or</span>
                    <div className="border-t border-border flex-1" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="biometric-login-btn"
                    disabled={biometricLoading}
                    onClick={handleBiometricLogin}
                    className="w-full h-12 text-base gap-3 border-primary/30 hover:border-primary hover:bg-primary/5"
                  >
                    <Fingerprint className="w-5 h-5 text-primary" />
                    {biometricLoading ? 'Verifying...' : 'Sign in with Face ID / Touch ID'}
                  </Button>
                </div>
              )}

              <p className="mt-6 text-center text-muted-foreground">
                {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                <button
                  onClick={() => {
                    setMode(mode === 'login' ? 'register' : 'login');
                    setError('');
                  }}
                  className="text-primary font-medium hover:underline"
                >
                  {mode === 'login' ? 'Sign up' : 'Sign in'}
                </button>
              </p>

              {mode === 'login' && (
                <p className="mt-3 text-center">
                  <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary">
                    Forgot your password?
                  </Link>
                </p>
              )}
              </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary" />
        <img 
          src="https://images.pexels.com/photos/7592335/pexels-photo-7592335.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Healthy lifestyle"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40"
        />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-md">
            <h2 className="text-3xl font-bold mb-4">Personalized for You</h2>
            <p className="text-white/80 text-lg">
              Get AI-powered supplement recommendations based on your unique health profile and goals.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
